/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/mock/campaignTemplateMock.js
 *
 * Description:
 * Mock data for Prospect Import Template.
 *
 * This file contains only static template information used by
 * the Download Template feature and Verify File Structure process.
 *
 * No business logic should be implemented in this file.
 * ============================================================================
 */

/**
 * --------------------------------------------------------------------------
 * Template Information
 * --------------------------------------------------------------------------
 */

export const templateInfoMock = {
    template_name: "Prospect Import Template",
    template_code: "PRS_IMPORT_V1",
    version: "1.0",
    sheet_name: "Prospects",
    file_name: "TaskFlow_Prospect_Import_Template.xlsx",
    description: "Standard template for importing prospects into Campaign Management."
};

/**
 * --------------------------------------------------------------------------
 * Required Columns
 * --------------------------------------------------------------------------
 */

export const requiredColumnsMock = [
    {
        field: "External ID",
        key: "external_id",
        required: true,
        max_length: 50,
        example: "EXT00001"
    },
    {
        field: "Company Name",
        key: "company_name",
        required: true,
        max_length: 200,
        example: "ABC Industries Pvt Ltd"
    },
    {
        field: "Contact Name",
        key: "contact_name",
        required: true,
        max_length: 150,
        example: "Rahul Sharma"
    },
    {
        field: "Mobile",
        key: "mobile",
        required: true,
        max_length: 20,
        example: "9876543210"
    },
    {
        field: "Email",
        key: "email",
        required: true,
        max_length: 150,
        example: "rahul@abc.com"
    }
];

/**
 * --------------------------------------------------------------------------
 * Optional Columns
 * --------------------------------------------------------------------------
 */

export const optionalColumnsMock = [
    {
        field: "Campaign",
        key: "campaign",
        example: "Website Leads"
    },
    {
        field: "Lead Source",
        key: "lead_source",
        example: "Website"
    },
    {
        field: "Industry",
        key: "industry",
        example: "Manufacturing"
    },
    {
        field: "Priority",
        key: "priority",
        example: "Medium"
    },
    {
        field: "Website",
        key: "website",
        example: "https://www.company.com"
    },
    {
        field: "GST Number",
        key: "gst_number",
        example: "29ABCDE1234F1Z5"
    },
    {
        field: "Address Line 1",
        key: "address1",
        example: "Industrial Area"
    },
    {
        field: "Address Line 2",
        key: "address2",
        example: "Phase II"
    },
    {
        field: "City",
        key: "city",
        example: "Bengaluru"
    },
    {
        field: "State",
        key: "state",
        example: "Karnataka"
    },
    {
        field: "Country",
        key: "country",
        example: "India"
    },
    {
        field: "PIN Code",
        key: "pincode",
        example: "560001"
    },
    {
        field: "Remarks",
        key: "remarks",
        example: "Imported from exhibition"
    }
];

/**
 * --------------------------------------------------------------------------
 * Validation Rules
 * --------------------------------------------------------------------------
 */

export const validationRulesMock = [
    {
        field: "External ID",
        rule: "Must be unique."
    },
    {
        field: "Company Name",
        rule: "Cannot be blank."
    },
    {
        field: "Contact Name",
        rule: "Cannot be blank."
    },
    {
        field: "Mobile",
        rule: "Must contain only digits."
    },
    {
        field: "Email",
        rule: "Must be a valid email address."
    },
    {
        field: "Website",
        rule: "Must be a valid URL."
    },
    {
        field: "Priority",
        rule: "Low / Medium / High only."
    }
];

/**
 * --------------------------------------------------------------------------
 * Sample Template Data
 * --------------------------------------------------------------------------
 */

export const sampleRowsMock = [
    {
        external_id: "EXT00001",
        company_name: "ABC Industries Pvt Ltd",
        contact_name: "Rahul Sharma",
        mobile: "9876543210",
        email: "rahul@abc.com",
        campaign: "Website Leads",
        lead_source: "Website",
        industry: "Manufacturing",
        priority: "Medium",
        city: "Pune"
    },
    {
        external_id: "EXT00002",
        company_name: "XYZ Technologies",
        contact_name: "Neha Kapoor",
        mobile: "9988776655",
        email: "neha@xyztech.com",
        campaign: "Trade Show",
        lead_source: "Exhibition",
        industry: "Software",
        priority: "High",
        city: "Bengaluru"
    }
];

/**
 * --------------------------------------------------------------------------
 * Download Response
 * --------------------------------------------------------------------------
 */

export const downloadTemplateResponseMock = {
    success: true,
    message: "Template generated successfully.",
    file_name: templateInfoMock.file_name,
    download_url: "/mock/templates/TaskFlow_Prospect_Import_Template.xlsx"
};

/**
 * --------------------------------------------------------------------------
 * Mock API Response
 * --------------------------------------------------------------------------
 */

export const campaignTemplateMockResponse = {
    success: true,
    template: templateInfoMock,
    required_columns: requiredColumnsMock,
    optional_columns: optionalColumnsMock,
    validation_rules: validationRulesMock,
    sample_rows: sampleRowsMock
};

export default {
    templateInfoMock,
    requiredColumnsMock,
    optionalColumnsMock,
    validationRulesMock,
    sampleRowsMock,
    downloadTemplateResponseMock,
    campaignTemplateMockResponse
};